<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-body">

                <!-- Global Override Alert -->
                <div v-if="globalTldSetting !== 0"
                    class="global-alert"
                    :class="{ 'alert-allow': globalTldSetting === 1, 'alert-block': globalTldSetting === 2 }">
                    <div class="d-flex align-items-center">
                        <i class="fa-solid fa-circle-info me-2" style="font-size: 18px;"></i>
                        <div>
                            <strong>{{ globalOverrideMessage }}</strong>
                            <div class="mt-1" style="font-size: 13px; opacity: 0.85;">
                                To manage individual TLDs, set "Government TLDs" to "Off" in
                                <a href="/account-settings" class="alert-link">Configuration <i class="fa-solid fa-arrow-right mx-1"></i> Settings</a>.
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Search + Counter -->
                <div class="row mb-3">
                    <div class="col-12 col-md-4">
                        <input v-model="searchQuery" type="text" class="form-control form-control-sm"
                            placeholder="&#xF002; Search TLDs..."
                            style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;">
                    </div>
                    <div class="col-12 col-md-8 d-flex align-items-center justify-content-end">
                        <small class="text-muted">{{ filteredTlds.length }} of {{ masterTldList.length }} TLDs</small>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="isLoading" class="d-flex justify-content-center my-5">
                    <div class="spinner-border" style="color: #00a0c1; width: 2.5rem; height: 2.5rem;" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <!-- TLDs Grid -->
                <div v-else class="row">
                    <div class="col-xl-6 col-lg-6 col-md-12 mb-2" v-for="tld in filteredTlds" :key="tld.Tld">
                        <div class="tld-row">
                            <div class="tld-label">
                                <span class="tld-name">{{ tld.Tld }}</span>
                                <span class="tld-desc">{{ tld.Description }}</span>
                            </div>
                            <div class="btn-group" role="group">
                                <button type="button"
                                    class="btn btn-outline-left btn-sm rounded-start-pill btn-check-left hand-cursor"
                                    @click="handleSingleTldChange(tld.Tld, 1)"
                                    :class="{ active: isAllowActive(tld) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[tld.Tld]">Allow</button>

                                <button type="button"
                                    class="btn btn-outline-center btn-sm btn-check-center hand-cursor"
                                    @click="handleSingleTldChange(tld.Tld, 0)"
                                    :class="{ active: isOffActive(tld) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[tld.Tld]">Off</button>

                                <button type="button"
                                    class="btn btn-outline-right btn-sm rounded-end-pill btn-check-right hand-cursor"
                                    @click="handleSingleTldChange(tld.Tld, 2)"
                                    :class="{ active: isBlockActive(tld) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[tld.Tld]">Block</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!isLoading && filteredTlds.length === 0" class="text-center text-muted py-4">
                    No TLDs match your search.
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isLoading: true,
            isSaving: false,
            masterTldList: [],
            tldSettings: {},
            globalTldSetting: 0,
            searchQuery: '',
            action_cooldowns: {},
        };
    },
    computed: {
        areButtonsDisabled() {
            return this.isSaving || this.globalTldSetting !== 0;
        },
        globalOverrideMessage() {
            if (this.globalTldSetting === 1) {
                return "Global Setting Active: All Government TLDs are set to ALLOW.";
            }
            if (this.globalTldSetting === 2) {
                return "Global Setting Active: All Government TLDs are set to BLOCK.";
            }
            return "";
        },
        filteredTlds() {
            if (!this.searchQuery) return this.masterTldList;
            const q = this.searchQuery.toLowerCase();
            return this.masterTldList.filter(tld =>
                tld.Tld.toLowerCase().includes(q) ||
                (tld.Description && tld.Description.toLowerCase().includes(q))
            );
        }
    },
    mounted() {
        this.fetchSettings();
    },
    methods: {
        isAllowActive(tld) {
            return this.globalTldSetting === 1 || (this.globalTldSetting === 0 && this.tldSettings[tld.Tld] === 1);
        },
        isOffActive(tld) {
            return this.globalTldSetting === 0 && (this.tldSettings[tld.Tld] === undefined || this.tldSettings[tld.Tld] === null || this.tldSettings[tld.Tld] === 0);
        },
        isBlockActive(tld) {
            return this.globalTldSetting === 2 || (this.globalTldSetting === 0 && this.tldSettings[tld.Tld] === 2);
        },

        setCooldown(key) {
            if (typeof this.$set === 'function') {
                this.$set(this.action_cooldowns, key, true);
                setTimeout(() => {
                    this.$delete(this.action_cooldowns, key);
                }, 1000);
            } else {
                this.action_cooldowns[key] = true;
                setTimeout(() => {
                    delete this.action_cooldowns[key];
                }, 1000);
            }
        },

        async fetchSettings() {
            this.isLoading = true;
            try {
                const response = await this.$axios.get("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-master-tlds");

                const allTlds = response.data.all_tlds || [];
                const userTldSettings = response.data.tld_settings || [];
                const accountSettings = response.data.account_settings || {};

                this.globalTldSetting = accountSettings.government_tld_setting || 0;
                this.masterTldList = allTlds;

                const userSettingsMap = userTldSettings.reduce((acc, setting) => {
                    acc[setting.tld] = setting.enabled_type;
                    return acc;
                }, {});

                const settings = {};
                for (const tld of allTlds) {
                    settings[tld.Tld] = userSettingsMap[tld.Tld] || 0;
                }
                this.tldSettings = settings;

            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Could not load settings.");
            } finally {
                this.isLoading = false;
            }
        },

        async handleSingleTldChange(tld, value) {
            if (this.areButtonsDisabled) return;
            if (this.action_cooldowns[tld]) return;
            if (this.tldSettings[tld] === value) return;

            this.setCooldown(tld);

            const oldValue = this.tldSettings[tld];
            this.tldSettings[tld] = value;
            this.isSaving = true;

            try {
                await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/update-government-tld-status", {
                    tld: tld,
                    enabled_type: value,
                });
                this.$toast.success(`Updated tld settings for ${tld}. Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.`);
            } catch (error) {
                this.tldSettings[tld] = oldValue;
                this.$toast.error(error.response?.data?.message || `Failed to update ${tld}.`);
            } finally {
                this.isSaving = false;
            }
        },

        async handleBulkChange(value) {
            this.isSaving = true;
            const originalSettings = { ...this.tldSettings };

            for (const tld in this.tldSettings) {
                this.tldSettings[tld] = value;
            }

            try {
                await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/sync-all-government-tlds-status", {
                    enabled_type: value,
                });
                this.$toast.success("All TLD settings updated successfully! Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.");
            } catch (error) {
                this.tldSettings = originalSettings;
                this.$toast.error(error.response?.data?.message || "Failed to update all settings.");
            } finally {
                this.isSaving = false;
            }
        },
    },
};
</script>

<style scoped>
.card {
    border: none;
}

/* Global alert */
.global-alert {
    padding: 14px 18px;
    border-radius: 10px;
    margin-bottom: 16px;
    font-size: 14px;
}

.global-alert.alert-allow {
    background-color: #e6f9fc;
    color: #006073;
    border: 1px solid #b8e8f0;
}

.global-alert.alert-block {
    background-color: #fef5ed;
    color: #9a6231;
    border: 1px solid #fbe0c4;
}

.global-alert .alert-link {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
}

/* TLD row layout */
.tld-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-radius: 8px;
    transition: background-color 0.15s ease;
}

.tld-row:hover {
    background-color: #f8f9fa;
}

.tld-label {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-right: 1rem;
}

.tld-name {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
}

.tld-desc {
    font-size: 12px;
    color: #6c757d;
    margin-top: 1px;
}

/* Button group styles */
.btn-check-left { min-width: 5rem; max-height: 2.2rem; border-color: gray; }
.btn-check-center { min-width: 3.5rem; max-height: 2.2rem; border-color: gray; }
.btn-check-right { min-width: 5rem; max-height: 2.2rem; border-color: gray; }

.btn-outline-right { color: #f3994d; background-color: transparent; border-color: grey }
.btn-outline-right:hover, .btn-outline-right.active, .btn-outline-right:active { color: whitesmoke; background-color: #f3994d; border-color: #f3994d; font-weight: bold;}

.btn-outline-center { color: #a3c0d1; background-color: transparent; border-color: grey }
.btn-outline-center:hover, .btn-outline-center.active, .btn-outline-center:active { color: whitesmoke; background-color: #a3c0d1; border-color: #a3c0d1; font-weight: bold;}

.btn-outline-left { color: #00a0c1; background-color: transparent; border-color: grey }
.btn-outline-left:hover, .btn-outline-left.active, .btn-outline-left:active { color: whitesmoke; background-color: #00a0c1; border-color: #00a0c1; font-weight: bold;}

.hand-cursor {
    cursor: pointer;
}
</style>